/**
 * Pipeline Service - Handles API interactions for pipeline templates and libraries
 */

import axios from 'axios';
import { get, post, put } from '../utils/apiUtils';

// No need to use BASE_URL as it's already configured in apiUtils.js
const API_URL = `/api/v1/mms_persistence/pipeline`;
const PIPELINE_EXEC_URL = `/api/v1/mms_persistence/pipeline_execution`;
const NODE_EXEC_URL = `/api/v1/mms_persistence/node_execution`;
const PIPELINE_URL = `/api/v1/bluemormon/pipeline`;
const WORKFLOW_EXEC_URL_V2 = `/api/v1/mms_ai_executors/workflow/execute/async`;
const PIPELINE_CONFIG_URL = `/api/v1/mms_ai_configs/pipeline/config`
const PIPELINE_CONFIG_Paginated_URL = `/api/v1/mms_ai_metric_events/metrics/runs/configs_with_aggregate_metrics`

const FILENODE_CONFIG = [];
/**
 * Get all pipelines with optional filtering
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Array>} Promise resolving to array of pipelines
 */
export const getPipelines = async (filters = {}, sort = {}, query = null) => {
  try {
    const response = await post(`${API_URL}/fetch?${query}`, { filter: filters,sort: sort });
    return response;
  } catch (error) {
    console.error('Error fetching pipelines:', error);
    throw error;
  }
};

/**
 * Get all pipelines with optional filtering
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Array>} Promise resolving to array of pipelines
 */
export const getPipelinesByPagination = async (filter = {}, sort = {}, query = {}) => {
  try {
    const {limit = 10, skip = 0} = query;
    const response = await post(`${PIPELINE_CONFIG_Paginated_URL}/pipeline?`, { filter, sort, limit, skip });
    return response;
  } catch (error) {
    console.error('Error fetching pipelines:', error);
    throw error;
  }
};

/**
 * Execute a workflow via v2 synchronous executor
 * @param {Object} payload - Execution payload
 * @returns {Promise<Object>} Promise resolving to execution result
 */
export const runExecutePipelineV2 = async (payload) => {
  try {
    const response = await post(WORKFLOW_EXEC_URL_V2, payload);
    return response;
  } catch (error) {
    console.error(`Error executing workflow via v2 executor:`, error);
    throw error;
  }
};

/**
 * Get a specific pipeline by ID
 * @param {string} pipelineId - Pipeline ID
 * @returns {Promise<Object>} Promise resolving to pipeline object
 */
export const getPipelineById = async (pipelineId) => {
  try {
    const response = await get(`${API_URL}/${pipelineId}`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching pipeline ${pipelineId}:`, error);
    throw error;
  }
};

/**
 * Create a new pipeline
 * @param {Object} pipelineData - Pipeline data
 * @returns {Promise<Object>} Promise resolving to created pipeline
 */
export const createPipeline = async (pipelineData) => {
  try {
    const response = await post(API_URL, pipelineData);
    return response;
  } catch (error) {
    console.error('Error creating pipeline:', error);
    throw error;
  }
};

/**
 * Update an existing pipeline
 * @param {string} pipelineId - Pipeline ID
 * @param {Object} pipelineData - Updated pipeline data
 * @returns {Promise<Object>} Promise resolving to updated pipeline
 */
export const updatePipeline = async (pipelineData) => {
  try {
    const response = await put(`${API_URL}`, pipelineData);
    return response;
  } catch (error) {
    console.error(`Error updating pipeline:`, error);
    throw error;
  }
};

/**
 * Delete a pipeline
 * @param {string} pipelineId - Pipeline ID
 * @returns {Promise<void>} Promise resolving when pipeline is deleted
 */
export const deletePipeline = async (pipelineId) => {
  try {
    await axios.delete(`${API_URL}/${pipelineId}`);
  } catch (error) {
    console.error(`Error deleting pipeline ${pipelineId}:`, error);
    throw error;
  }
};

/**
 * Get pipeline variables
 * @param {string} pipelineId - Pipeline ID
 * @returns {Promise<Array>} Promise resolving to array of pipeline variables
 */
export const getPipelineVariables = async (pipelineId) => {
  try {
    const response = await axios.get(`${API_URL}/${pipelineId}/variables`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching variables for pipeline ${pipelineId}:`, error);
    throw error;
  }
};

/**
 * Update pipeline variables
 * @param {string} pipelineId - Pipeline ID
 * @param {Array} variables - Updated variables
 * @returns {Promise<Object>} Promise resolving to updated pipeline
 */
export const updatePipelineVariables = async (pipelineId, variables) => {
  try {
    const response = await axios.put(`${API_URL}/${pipelineId}/variables`, { variables });
    return response.data;
  } catch (error) {
    console.error(`Error updating variables for pipeline ${pipelineId}:`, error);
    throw error;
  }
};

/**
 * Get pipeline examples
 * @param {string} pipelineId - Pipeline ID
 * @returns {Promise<Array>} Promise resolving to array of pipeline examples
 */
export const getPipelineExamples = async (pipelineId) => {
  try {
    const response = await axios.get(`${API_URL}/${pipelineId}/examples`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching examples for pipeline ${pipelineId}:`, error);
    throw error;
  }
};

/**
 * Add a pipeline example
 * @param {string} pipelineId - Pipeline ID
 * @param {Object} example - Example data
 * @returns {Promise<Object>} Promise resolving to created example
 */
export const addPipelineExample = async (pipelineId, example) => {
  try {
    const response = await axios.post(`${API_URL}/${pipelineId}/examples`, example);
    return response.data;
  } catch (error) {
    console.error(`Error adding example to pipeline ${pipelineId}:`, error);
    throw error;
  }
};

/**
 * Delete a pipeline example
 * @param {string} pipelineId - Pipeline ID
 * @param {string} exampleId - Example ID
 * @returns {Promise<void>} Promise resolving when example is deleted
 */
export const deletePipelineExample = async (pipelineId, exampleId) => {
  try {
    await axios.delete(`${API_URL}/${pipelineId}/examples/${exampleId}`);
  } catch (error) {
    console.error(`Error deleting example ${exampleId} from pipeline ${pipelineId}:`, error);
    throw error;
  }
};

/**
 * Execute a pipeline with provided variables
 * @param {string} pipelineId - Pipeline ID
 * @param {Object} variables - Variables to use in the pipeline
 * @returns {Promise<Object>} Promise resolving to execution result
 */
export const runExecutePipeline = async (pipelineId, variables) => {
  try {
    const response = await post(`${PIPELINE_URL}/${pipelineId}`,  variables);
    return response;
  } catch (error) {
    console.error(`Error executing pipeline ${pipelineId}:`, error);
    throw error;
  }
};
/**
 * Get all WorkStreams
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Array>} Promise resolving to array of pipelines
 */
export const getWorkStreams = async (filters = {}, sort = {}, query = null) => {
  try {
    const response = await post(`/api/v1/mms_persistence/workstream/fetch?${query}`, { filter: filters,sort: sort });
    return response;
  } catch (error) {
    console.error('Error fetching Workstreams:', error);
    throw error;
  }
};
/**
 * Resume a pipeline execution
 * @param {string} pipelineId - Pipeline ID
 * @param {Object} variables - Variables and input data
 * @returns {Promise<Object>} Promise resolving to execution result
 */
export const resumePipeline = async (pipelineId, variables) => {
  try {
    const response = await put(`${PIPELINE_URL}/${pipelineId}`, variables);
    return response;
  } catch (error) {
    console.error(`Error resuming pipeline ${pipelineId}:`, error);
    throw error;
  }
};

/**
 * Get pipeline usage statistics
 * @param {string} pipelineId - Pipeline ID
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Object>} Promise resolving to usage statistics
 */
export const getPipelineUsage = async (pipelineId, filters = {}) => {
  try {
    const response = await axios.get(`${API_URL}/${pipelineId}/usage`, { params: filters });
    return response.data;
  } catch (error) {
    console.error(`Error fetching usage for pipeline ${pipelineId}:`, error);
    throw error;
  }
};

/**
 * Get pipeline categories
 * @returns {Promise<Array>} Promise resolving to array of pipeline categories
 */
export const getPipelineCategories = async () => {
  try {
    const response = await axios.get(`${API_URL}/categories`);
    return response.data;
  } catch (error) {
    console.error('Error fetching pipeline categories:', error);
    throw error;
  }
};

/**
 * Create a new pipeline category
 * @param {Object} categoryData - Category data
 * @returns {Promise<Object>} Promise resolving to created category
 */
export const createPipelineCategory = async (categoryData) => {
  try {
    const response = await axios.post(`${API_URL}/categories`, categoryData);
    return response.data;
  } catch (error) {
    console.error('Error creating pipeline category:', error);
    throw error;
  }
};

/**
 * Get pipeline feedback
 * @param {string} pipelineId - Pipeline ID
 * @returns {Promise<Array>} Promise resolving to array of feedback
 */
export const getPipelineFeedback = async (pipelineId) => {
  try {
    const response = await axios.get(`${API_URL}/${pipelineId}/feedback`);
    return response.data;
  } catch (error) {
    console.error(`Error fetching feedback for pipeline ${pipelineId}:`, error);
    throw error;
  }
};

/**
 * Add feedback for a pipeline
 * @param {string} pipelineId - Pipeline ID
 * @param {Object} feedback - Feedback data
 * @returns {Promise<Object>} Promise resolving to created feedback
 */
export const addPipelineFeedback = async (pipelineId, feedback) => {
  try {
    const response = await axios.post(`${API_URL}/${pipelineId}/feedback`, feedback);
    return response.data;
  } catch (error) {
    console.error(`Error adding feedback to pipeline ${pipelineId}:`, error);
    throw error;
  }
};

/**
 * Get all pipelines Analytics
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Array>} Promise resolving to array of pipelines
 */
export const getPipelineRuns = async (filters = {}, sort = {}, query = null, fields = undefined) => {
  try {
    const payload = fields ? { filter: filters, sort: sort, fields } : { filter: filters, sort: sort };
    const response = await post(`${PIPELINE_EXEC_URL}/fetch?${query}`, payload);
    return response;
  } catch (error) {
    console.error('Error fetching pipelines:', error);
    throw error;
  }
};

/**
 * Get all pipelines Analytics
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Array>} Promise resolving to array of pipelines
 */
// Track requests to prevent duplicates
const pendingRequests = {};

export const getNodesForPipeline = async (filters = {}, sort = {}, query = null, requestId = null, fields = undefined) => {
  if (!query) {
    query = '?skip=0&limit=1';
  }
  
  // Create a unique key for this request
  const requestKey = requestId || 
    `${JSON.stringify(filters)}_${JSON.stringify(sort)}_${query}`;
  
  // Cancel previous pending request with the same key
  if (pendingRequests[requestKey]) {
    console.log(`Canceling previous request: ${requestKey}`);
    pendingRequests[requestKey].cancel('Request superseded by newer request');
    delete pendingRequests[requestKey];
  }
  
  // Create a new CancelToken
  const CancelToken = axios.CancelToken;
  const source = CancelToken.source();
  
  // Store the cancel token
  pendingRequests[requestKey] = source;
  
  try {
    const body = fields ? { filter: filters, sort: sort, fields } : { filter: filters, sort: sort };
    const response = await post(
      `${NODE_EXEC_URL}/fetch${query}`, 
      body,
      { cancelToken: source.token }
    );
    
    // Request completed successfully, remove from pending
    delete pendingRequests[requestKey];
    return response;
  } catch (error) {
    // Don't log canceled requests as errors
    if (!axios.isCancel(error)) {
      console.error('Error fetching pipelines:', error);
    } else {
      console.log('Request canceled:', error.message);
    }
    throw error;
  }
};

/**
 * Evaluate Expression of Node
 * @param {Object} payload - Filter parameters
 * @returns {Promise<Array>} Promise resolving to array of pipelines
 */
export const evaluateNodeExpression = async (payload = {
  "code_block": [],
  "request": {},
  "workflow_vars": {},
  "verify_bool": false
}) => {
  try {
    const response = await put(`${PIPELINE_URL}/evaluate/expression`,  payload );
    return response;
  } catch (error) {
    console.error('Error fetching pipelines:', error);
    throw error;
  }
};

/**
 * Get pipeline execution status
 * @param {string} pipelineId - Pipeline execution ID
 * @returns {Promise<Object>} Promise resolving to pipeline execution status
 */
export const getPipelineExecutionStatus = async (pipelineId) => {
  try {
    const response = await post(`${PIPELINE_EXEC_URL}/fetch`, { 
      filter: { pipeline_id: pipelineId },
      sort: { created_at: "DESC" }
    });
    return response && response.length > 0 ? response[0] : null;
  } catch (error) {
    console.error(`Error fetching pipeline execution status for ${pipelineId}:`, error);
    throw error;
  }
};

/**
 * Get pipeline count
 * @param {string} pipelineId - Pipeline execution ID
 * @returns {Promise<Object>} Promise resolving to pipeline execution status
 */
export const getPipelineCount = async () => {
   try {
     const response = await post(`${API_URL}/count`, {});
     return response.count;
   } catch (error) {
     console.error('Error fetching knowledge bases:', error);
     throw error;
   }
};


/**
 * Get pipeline count
 * @param {string} pipelineId - Pipeline execution ID
 * @returns {Promise<Object>} Promise resolving to pipeline execution status
 */
export const getExecutionPipelineCount = async (filter = {}) => {
   try {
     const response = await post(`${PIPELINE_EXEC_URL}/count`, filter);
     return response.count;
   } catch (error) {
     console.error('Error fetching knowledge bases:', error);
     throw error;
   }
};



/**
 * Get file input node config
 * @returns {Promise<Object>} Promise resolving to pipeline execution status
 */
export const getNodeConfig = async (nodeType) => {
   try {
     const response = await get(`${PIPELINE_URL}/${nodeType}/metadata/`);
     return response;
   } catch (error) {
     console.error('Error fetching file input node config:', error);
     throw error;
   }
};



export default {
  getPipelines,
  getPipelineById,
  getPipelineRuns,
  getNodesForPipeline,
  createPipeline,
  updatePipeline,
  deletePipeline,
  getPipelineVariables,
  updatePipelineVariables,
  getPipelineExamples,
  addPipelineExample,
  deletePipelineExample,
  runExecutePipeline,
  runExecutePipelineV2,
  resumePipeline,
  getPipelineExecutionStatus,
  getPipelineUsage,
  getPipelineCategories,
  createPipelineCategory,
  getPipelineFeedback,
  addPipelineFeedback,
  getPipelineExecutionStatus
};

